import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CaretDown, UserCircle, SignOut, Plus, CurrencyCircleDollar, Receipt, TrendDown, TrendUp, ChartBar, MagnifyingGlass, Notepad, ArrowsClockwise, Trash, X, Copy } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Logo from '../components/Logo';
import MonthPicker from '../components/MonthPicker';
import Avatar from '../components/Avatar';
import NotificationBell from '../components/NotificationBell';
import { formatMoney, getCurrency } from '../utils/currency';
import { DEFAULT_CATEGORIES, getCategoryColor } from '../utils/categories';

export default function Dashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [total, setTotal] = useState(0);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [previousTotal, setPreviousTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [toast, setToast] = useState(null);
  const [userMenu, setUserMenu] = useState(false);
  const menuRef = useRef(null);
  const [lastAddedId, setLastAddedId] = useState(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [recurrence, setRecurrence] = useState('none');
  const [recurrenceEnd, setRecurrenceEnd] = useState('');
  const [notes, setNotes] = useState('');
  const [installments, setInstallments] = useState('');
  const [userCategories, setUserCategories] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');

  const cs = getCurrency(user);

  function showToast(message, type = 'error') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadExpenses = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/expenses/${year}/${month}`);
      setExpenses(data.expenses);
      setTotal(data.total);
      setPreviousTotal(data.previousTotal);
    } catch {
      console.error('Erro ao carregar despesas');
    } finally {
      setLoading(false);
    }
  }, [year, month]);

  useEffect(() => {
    api.get('/categories').then(({ data }) => setUserCategories(data)).catch(() => {});
  }, []);

  useEffect(() => {
    loadExpenses();
  }, [loadExpenses]);

  async function handleAdd(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: created } = await api.post('/expenses', {
        description,
        amount: parseFloat(amount),
        category: category || 'Geral',
        date,
        recurrence,
        ...(recurrenceEnd && recurrence !== 'none' && { recurrenceEnd }),
        ...(notes.trim() && { notes: notes.trim() }),
        ...(installments && parseInt(installments) > 1 && { installments: parseInt(installments) }),
      });
      setLastAddedId(created.id || created.installmentId);
      setTimeout(() => setLastAddedId(null), 1000);
      setDescription('');
      setAmount('');
      setCategory('');
      setShowModal(false);
      loadExpenses();
    } catch (err) {
      const data = err.response?.data;
      if (data?.upgradeRequired) {
        showToast(data.error, 'upgrade');
      } else {
        showToast(data?.error || 'Erro ao adicionar despesa');
      }
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const id = deleteTarget.sourceId || deleteTarget.id;
      await api.delete(`/expenses/${id}`);
      setDeleteTarget(null);
      loadExpenses();
    } catch {
      showToast('Erro ao remover despesa');
    } finally {
      setDeleting(false);
    }
  }

  async function handleSaveEdit() {
    if (!selectedExpense) return;
    setSavingEdit(true);
    try {
      const id = selectedExpense.sourceId || selectedExpense.id;
      await api.put(`/expenses/${id}`, {
        description: editData.description,
        amount: parseFloat(editData.amount),
        category: editData.category,
        date: editData.date,
        recurrenceEnd: editData.recurrenceEnd || null,
        notes: editData.notes || null,
      });
      setEditing(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch {
      showToast('Erro ao atualizar despesa');
    } finally {
      setSavingEdit(false);
    }
  }

  async function handleExcludeOccurrence() {
    if (!deleteTarget?.sourceId) return;
    setDeleting(true);
    try {
      await api.post(`/expenses/${deleteTarget.sourceId}/exclude`, {
        date: new Date(deleteTarget.date).toISOString().split('T')[0],
      });
      setDeleteTarget(null);
      loadExpenses();
    } catch {
      showToast('Erro ao remover ocorrencia');
    } finally {
      setDeleting(false);
    }
  }

  function handleMonthChange(m, y) {
    setMonth(m);
    setYear(y);
  }

  // Filtrar despesas
  const filteredExpenses = expenses.filter(e => {
    if (search && !e.description.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterCategory && (e.category || 'Geral') !== filterCategory) return false;
    return true;
  });

  // Agrupar por categoria
  const categories = {};
  expenses.forEach(e => {
    const cat = e.category || 'Geral';
    categories[cat] = (categories[cat] || 0) + parseFloat(e.amount);
  });


  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50/50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200/60 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <Logo linkTo="/dashboard" />

          <div className="flex items-center gap-1">
          <NotificationBell />
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setUserMenu(!userMenu)}
              className="flex items-center gap-2.5 hover:bg-slate-100 rounded-xl px-3 py-1.5 transition"
            >
              <Avatar user={user} size="sm" />
              <span className="text-sm font-medium text-slate-600 hidden sm:block">{user?.name}</span>
              <CaretDown size={14} className={`text-slate-400 transition-transform ${userMenu ? 'rotate-180' : ''}`} weight="bold" />
            </button>

            {userMenu && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white rounded-xl shadow-lg border border-slate-200/60 overflow-hidden z-50">
                <div className="px-4 py-3 border-b border-slate-100">
                  <p className="text-sm font-semibold text-slate-800">{user?.name}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{user?.email}</p>
                </div>
                <button
                  onClick={() => { setUserMenu(false); navigate('/account'); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-slate-600 hover:bg-slate-50 transition flex items-center gap-2"
                >
                  <UserCircle size={16} />
                  Minha conta
                </button>
                <button
                  onClick={() => { logout(); navigate('/'); }}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition flex items-center gap-2"
                >
                  <SignOut size={16} />
                  Sair da conta
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Navegacao de mes + botao */}
        <div className="flex items-center justify-between mb-6 sm:mb-8">
          <MonthPicker month={month} year={year} onChange={handleMonthChange} disabled={loading} />

          <button
            onClick={() => { setDescription(''); setAmount(''); setCategory('Geral'); setDate(new Date().toISOString().split('T')[0]); setRecurrence('none'); setRecurrenceEnd(''); setNotes(''); setInstallments(''); setShowModal(true); }}
            className="bg-blue-600 text-white px-4 py-2.5 rounded-xl hover:bg-blue-700 active:bg-blue-800 transition text-sm font-medium shadow-sm flex items-center gap-2"
          >
            <Plus size={16} weight="bold" />
            <span className="hidden sm:inline">Nova despesa</span>
          </button>
        </div>

        <div className={`transition-opacity duration-200 ${loading ? 'opacity-40 pointer-events-none' : 'opacity-100'}`}>

        {/* Cards de resumo */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
          {/* Total do mes */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-blue-50 flex items-center justify-center mb-2 sm:mb-3">
              <CurrencyCircleDollar size={18} className="text-blue-600" />
            </div>
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Total do mes</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-800 truncate">{formatMoney(total, user)}</p>
          </div>

          {/* Despesas */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-amber-50 flex items-center justify-center mb-2 sm:mb-3">
              <Receipt size={18} className="text-amber-600" />
            </div>
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Despesas</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-800">{expenses.length}</p>
          </div>

          {/* Comparativo */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-xl flex items-center justify-center mb-2 sm:mb-3 ${
              total - previousTotal <= 0 ? 'bg-emerald-50' : 'bg-rose-50'
            }`}>
              {total - previousTotal <= 0 ? (
                <TrendDown size={18} className="text-emerald-600" />
              ) : (
                <TrendUp size={18} className="text-rose-500" />
              )}
            </div>
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">vs. mes anterior</p>
            <p className={`text-lg sm:text-2xl font-bold truncate ${
              total - previousTotal <= 0 ? 'text-emerald-600' : 'text-rose-500'
            }`}>
              {total - previousTotal <= 0 ? '-' : '+'}{formatMoney(Math.abs(total - previousTotal), user)}
            </p>
          </div>

          {/* Maior despesa */}
          <div className="bg-white rounded-2xl p-4 sm:p-5 shadow-sm border border-slate-200/60">
            <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-xl bg-rose-50 flex items-center justify-center mb-2 sm:mb-3">
              <TrendUp size={18} className="text-rose-500" />
            </div>
            <p className="text-[11px] sm:text-xs font-medium text-slate-400 mb-1">Maior despesa</p>
            <p className="text-lg sm:text-2xl font-bold text-slate-800 truncate">
              {expenses.length > 0
                ? formatMoney(Math.max(...expenses.map(e => parseFloat(e.amount))), user)
                : '--'}
            </p>
          </div>
        </div>

        {/* Limite de despesas - Free (so no mes atual) */}
        {user?.plan !== 'pro' && year === new Date().getFullYear() && month === new Date().getMonth() + 1 && expenses.length > 0 && (() => {
          const limit = user?.planLimits?.expensesPerMonth || 10;
          const remaining = Math.max(limit - expenses.length, 0);
          return (
            <p className="text-xs text-slate-400 mb-4">
              {remaining <= 0
                ? <span>Limite de {limit} despesas atingido. <button onClick={() => navigate('/account?tab=plans')} className="text-blue-600 hover:text-blue-800 font-medium transition">Assine o Pro para ilimitadas</button></span>
                : <span>{expenses.length} de {limit} despesas usadas{remaining <= 3 ? <span> — <button onClick={() => navigate('/account?tab=plans')} className="text-blue-600 hover:text-blue-800 font-medium transition">assine o Pro</button></span> : ''}</span>
              }
            </p>
          );
        })()}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Lista de despesas */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden flex flex-col" style={{ maxHeight: 'calc(100vh - 340px)' }}>
              <div className="px-4 sm:px-6 py-4 border-b border-slate-100 flex-shrink-0">
                <div className="flex items-center justify-between mb-3 gap-2">
                  <h3 className="font-semibold text-slate-800 shrink-0">Despesas</h3>
                  <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
                    <span className="text-[11px] sm:text-xs font-medium text-slate-400 bg-slate-100 px-2 sm:px-2.5 py-1 rounded-lg">
                      {filteredExpenses.length} ite{filteredExpenses.length !== 1 ? 'ns' : 'm'}
                    </span>
                    <Link
                      to={`/report?month=${month}&year=${year}`}
                      className="text-[11px] sm:text-xs font-medium text-slate-400 bg-slate-100 hover:bg-slate-200 px-2 sm:px-2.5 py-1 rounded-lg transition flex items-center gap-1"
                    >
                      <ChartBar size={14} />
                      <span className="hidden sm:inline">Relatorio</span>
                    </Link>
                    <Link
                      to="/year"
                      className="text-[11px] sm:text-xs font-medium text-slate-400 bg-slate-100 hover:bg-slate-200 px-2 sm:px-2.5 py-1 rounded-lg transition flex items-center gap-1"
                    >
                      <ChartBar size={14} />
                      <span className="hidden sm:inline">Anual</span>
                      {user?.plan !== 'pro' && <span className="text-[8px] font-bold text-blue-600 bg-blue-50 px-1 py-0.5 rounded">PRO</span>}
                    </Link>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <MagnifyingGlass size={16} className="text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Buscar despesa..."
                      className="w-full pl-9 pr-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-xs placeholder:text-slate-400 transition"
                    />
                  </div>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-600 focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition"
                  >
                    <option value="">Todas</option>
                    {Object.keys(categories).sort().map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="overflow-y-auto flex-1">
                {filteredExpenses.length === 0 ? (
                  <div className="p-16 text-center">
                    <div className="w-14 h-14 rounded-2xl bg-blue-50 flex items-center justify-center mx-auto mb-4">
                      <Notepad size={28} className="text-blue-300" />
                    </div>
                    <p className="text-slate-500 font-medium">{search || filterCategory ? 'Nenhuma despesa encontrada' : 'Nenhuma despesa neste mes'}</p>
                    <p className="text-sm text-slate-400 mt-1">{search || filterCategory ? 'Tente alterar os filtros' : 'Comece adicionando sua primeira despesa'}</p>
                  </div>
                ) : (
                  <div>
                    {filteredExpenses.map((expense, index) => (
                      <div
                        key={expense.id}
                        className={`flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 hover:bg-slate-50/60 transition group cursor-pointer ${
                          index < filteredExpenses.length - 1 ? 'border-b border-slate-50' : ''
                        } ${lastAddedId && (expense.id === lastAddedId || expense.installmentId === lastAddedId) ? 'animate-slide-in' : ''}`}
                        onClick={() => setSelectedExpense(expense)}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2.5 flex-wrap">
                            <p className="text-sm font-medium text-slate-800 break-all">{expense.description}</p>
                            <span
                              className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                              style={{ color: getCategoryColor(expense.category, userCategories), backgroundColor: getCategoryColor(expense.category, userCategories) + '18' }}
                            >
                              {expense.category}
                            </span>
                            {expense.installments && (
                              <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md font-medium flex items-center gap-1">
                                <Copy size={12} />
                                {expense.installmentNum}/{expense.installments}
                              </span>
                            )}
                            {expense.recurrence !== 'none' && (
                              <span className="text-[11px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded-md font-medium flex items-center gap-1">
                                <ArrowsClockwise size={12} />
                                {{ daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[expense.recurrence]}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <p className="text-xs text-slate-400">
                              {new Date(expense.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' })}
                            </p>
                            {expense.notes && (
                              <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded font-medium truncate max-w-[100px] sm:max-w-[150px] flex items-center gap-1">
                                <Notepad size={10} className="shrink-0" />
                                {expense.notes}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <p className="text-sm font-semibold text-slate-800">
                            {formatMoney(expense.amount, user)}
                          </p>
                          <button
                            onClick={(e) => { e.stopPropagation(); setDeleteTarget(expense); }}
                            className="p-1.5 rounded-lg text-slate-300 hover:text-red-400 hover:bg-red-50 transition sm:opacity-0 sm:group-hover:opacity-100"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Por categoria */}
          <div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200/60 overflow-hidden">
              <div className="px-4 sm:px-6 py-4 border-b border-slate-100">
                <h3 className="font-semibold text-slate-800">Por categoria</h3>
              </div>

              {Object.keys(categories).length === 0 ? (
                <p className="p-8 text-center text-sm text-slate-400">Sem dados</p>
              ) : (
                <div className="p-5 space-y-4">
                  {Object.entries(categories)
                    .sort(([, a], [, b]) => b - a)
                    .map(([cat, value]) => {
                      const percent = total > 0 ? Math.round(value / total * 100) : 0;
                      return (
                        <div key={cat}>
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-slate-700">{cat}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">{percent}%</span>
                              <span className="text-sm font-semibold text-slate-800">{formatMoney(value, user)}</span>
                            </div>
                          </div>
                          <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all duration-700 ease-out"
                              style={{ width: `${percent}%`, backgroundColor: getCategoryColor(cat, userCategories) }}
                            />
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </main>

      {/* Modal nova despesa */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setShowModal(false)} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <div>
                <h3 className="text-lg font-bold text-slate-800">Nova despesa</h3>
                <p className="text-sm text-slate-400 mt-0.5">Preencha os dados abaixo</p>
              </div>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleAdd} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Descricao</label>
                <input
                  type="text"
                  placeholder="Ex: Aluguel, Internet..."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  autoFocus
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Valor ({cs.symbol})</label>
                <input
                  type="number"
                  placeholder="0,00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                  step="0.01"
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Categoria</label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                  >
                    {DEFAULT_CATEGORIES.map(c => (
                      <option key={c.name} value={c.name}>{c.name}</option>
                    ))}
                    {userCategories.filter(c => !DEFAULT_CATEGORIES.some(d => d.name === c.name)).map(cat => (
                      <option key={cat.id} value={cat.name}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Data</label>
                  <input
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                  />
                </div>
              </div>
              {!installments && (
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                    Repetir
                    {user?.plan !== 'pro' && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">PRO</span>}
                  </label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {[
                      { value: 'none', label: 'Nao repete' },
                      { value: 'daily', label: 'Diaria' },
                      { value: 'weekly', label: 'Semanal' },
                      { value: 'monthly', label: 'Mensal' },
                      { value: 'yearly', label: 'Anual' },
                    ].map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setRecurrence(opt.value)}
                        className={`py-2 rounded-lg text-xs font-medium transition ${
                          recurrence === opt.value
                            ? 'bg-blue-600 text-white'
                            : 'bg-slate-50 border border-slate-200 text-slate-500 hover:bg-slate-100'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {recurrence === 'none' && (
                <div>
                  <label className="text-sm font-medium text-slate-600 mb-1.5 flex items-center gap-1.5">
                    Parcelas (opcional)
                    {user?.plan !== 'pro' && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">PRO</span>}
                  </label>
                  <div className="flex items-center gap-2">
                    <select
                      value={installments}
                      onChange={(e) => setInstallments(e.target.value)}
                      className="flex-1 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                    >
                      <option value="">A vista</option>
                      {[2,3,4,5,6,7,8,9,10,11,12,18,24,36,48].map(n => (
                        <option key={n} value={n}>{n}x de {amount ? formatMoney(parseFloat(amount) / n, user) : `${n}x`}</option>
                      ))}
                    </select>
                  </div>
                  {installments && amount && (
                    <p className="text-xs text-slate-400 mt-1.5">
                      Total: {formatMoney(amount, user)} em {installments}x de {formatMoney(parseFloat(amount) / parseInt(installments), user)}
                    </p>
                  )}
                </div>
              )}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                    Notas (opcional)
                    {user?.plan !== 'pro' && <span className="text-[9px] font-bold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">PRO</span>}
                  </label>
                  <span className="text-xs text-slate-400">{notes.length}/100</span>
                </div>
                <input
                  type="text"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Ex: dividido com fulano..."
                  maxLength={100}
                  className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
                />
              </div>
              {recurrence !== 'none' && (
                <div>
                  <label className="block text-sm font-medium text-slate-600 mb-1.5">Repetir ate (opcional)</label>
                  <input
                    type="date"
                    value={recurrenceEnd}
                    onChange={(e) => setRecurrenceEnd(e.target.value)}
                    min={date}
                    className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                  />
                  {!recurrenceEnd && (
                    <p className="text-xs text-slate-400 mt-1">Sem limite definido</p>
                  )}
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  disabled={saving}
                  className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium disabled:opacity-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 transition text-sm font-medium shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {saving && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  {saving ? 'Adicionando...' : 'Adicionar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de confirmacao de exclusao */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setDeleteTarget(null)} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
              <Trash size={24} className="text-red-500" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 text-center mb-1">Remover despesa</h3>
            <p className="text-sm text-slate-400 text-center mb-2">
              Tem certeza que deseja remover <span className="font-medium text-slate-600">"{deleteTarget.description}"</span> de {formatMoney(deleteTarget.amount, user)}?
            </p>
            {total > 0 && (
              <p className="text-xs text-slate-400 text-center mb-6">
                O total do mes vai de {formatMoney(total, user)} para {formatMoney(total - parseFloat(deleteTarget.amount), user)} ({Math.round((parseFloat(deleteTarget.amount) / total) * 100)}% do total)
              </p>
            )}
            <div className="space-y-2">
              {deleteTarget.isVirtual ? (
                <>
                  <button
                    onClick={handleExcludeOccurrence}
                    disabled={deleting}
                    className="w-full bg-red-500 text-white py-2.5 rounded-xl hover:bg-red-600 transition text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {deleting && (
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                    )}
                    Remover apenas este mes
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={deleting}
                    className="w-full bg-red-100 text-red-600 py-2.5 rounded-xl hover:bg-red-200 transition text-sm font-medium disabled:opacity-50"
                  >
                    Cancelar toda recorrencia
                  </button>
                </>
              ) : (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="w-full bg-red-500 text-white py-2.5 rounded-xl hover:bg-red-600 transition text-sm font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {deleting && (
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                  )}
                  Remover despesa
                </button>
              )}
              <button
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
                className="w-full py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium disabled:opacity-50"
              >
                Voltar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes/edicao da despesa */}
      {selectedExpense && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => { setSelectedExpense(null); setEditing(false); }} />

          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 overflow-hidden">
            <div className="px-6 py-5 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-bold text-slate-800">{editing ? 'Editar despesa' : 'Detalhes da despesa'}</h3>
              <button
                onClick={() => { setSelectedExpense(null); setEditing(false); }}
                className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {editing ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Descricao</label>
                    <input
                      type="text"
                      value={editData.description}
                      onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Valor ({cs.symbol})</label>
                    <input
                      type="number"
                      step="0.01"
                      value={editData.amount}
                      onChange={(e) => setEditData({ ...editData, amount: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Categoria</label>
                    <select
                      value={editData.category}
                      onChange={(e) => setEditData({ ...editData, category: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                    >
                      {DEFAULT_CATEGORIES.map(c => (
                        <option key={c.name} value={c.name}>{c.name}</option>
                      ))}
                      {userCategories.filter(c => !DEFAULT_CATEGORIES.some(d => d.name === c.name)).map(cat => (
                        <option key={cat.id} value={cat.name}>{cat.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-600 mb-1.5">Data</label>
                    <input
                      type="date"
                      value={editData.date}
                      onChange={(e) => setEditData({ ...editData, date: e.target.value })}
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-sm font-medium text-slate-600">Notas (opcional)</label>
                      <span className="text-xs text-slate-400">{(editData.notes || '').length}/100</span>
                    </div>
                    <input
                      type="text"
                      value={editData.notes}
                      onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                      placeholder="Ex: dividido com fulano..."
                      className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm placeholder:text-slate-400 transition"
                    />
                  </div>
                  {editData.recurrence && editData.recurrence !== 'none' && (
                    <div>
                      <label className="block text-sm font-medium text-slate-600 mb-1.5">Repetir ate (opcional)</label>
                      <input
                        type="date"
                        value={editData.recurrenceEnd}
                        onChange={(e) => setEditData({ ...editData, recurrenceEnd: e.target.value })}
                        min={editData.date}
                        className="w-full px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 text-sm transition"
                      />
                      {!editData.recurrenceEnd && (
                        <p className="text-xs text-slate-400 mt-1">Sem limite definido</p>
                      )}
                    </div>
                  )}
                  <div className="flex gap-3 pt-2">
                    <button
                      onClick={() => setEditing(false)}
                      disabled={savingEdit}
                      className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={handleSaveEdit}
                      disabled={savingEdit}
                      className="flex-1 bg-blue-600 text-white py-2.5 rounded-xl hover:bg-blue-700 transition text-sm font-medium shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {savingEdit && (
                        <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                      )}
                      {savingEdit ? 'Salvando...' : 'Salvar'}
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="text-center mb-6">
                    <p className="text-3xl font-bold text-slate-800">{formatMoney(selectedExpense.amount, user)}</p>
                    <p className="text-sm text-slate-400 mt-1">{selectedExpense.description}</p>
                  </div>

                  <div className="space-y-4">
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-400">Categoria</span>
                      <span
                        className="text-sm font-medium px-2.5 py-0.5 rounded-md"
                        style={{ color: getCategoryColor(selectedExpense.category, userCategories), backgroundColor: getCategoryColor(selectedExpense.category, userCategories) + '18' }}
                      >{selectedExpense.category}</span>
                    </div>
                    {selectedExpense.recurrence !== 'none' && (
                      <>
                        <div className="flex items-center justify-between py-3 border-b border-slate-100">
                          <span className="text-sm text-slate-400">Recorrencia</span>
                          <span className="text-sm font-medium text-slate-800">
                            {{ daily: 'Diaria', weekly: 'Semanal', monthly: 'Mensal', yearly: 'Anual' }[selectedExpense.recurrence]}
                          </span>
                        </div>
                        <div className="flex items-center justify-between py-3 border-b border-slate-100">
                          <span className="text-sm text-slate-400">Repete ate</span>
                          <span className="text-sm font-medium text-slate-800">
                            {selectedExpense.recurrenceEnd
                              ? new Date(selectedExpense.recurrenceEnd).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' })
                              : 'Sem limite'}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-400">Data</span>
                      <span className="text-sm font-medium text-slate-800">
                        {new Date(selectedExpense.date).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' })}
                      </span>
                    </div>
                    {selectedExpense.notes && (
                      <div className="flex items-center justify-between py-3 border-b border-slate-100">
                        <span className="text-sm text-slate-400">Notas</span>
                        <span className="text-sm font-medium text-slate-800 max-w-[200px] text-right">{selectedExpense.notes}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between py-3 border-b border-slate-100">
                      <span className="text-sm text-slate-400">Adicionada em</span>
                      <span className="text-sm font-medium text-slate-800">
                        {new Date(selectedExpense.createdAt).toLocaleDateString('pt-BR', { day: '2-digit', month: 'long', year: 'numeric', timeZone: 'UTC' })}
                      </span>
                    </div>
                    {total > 0 && (
                      <div className="flex items-center justify-between py-3">
                        <span className="text-sm text-slate-400">% do total mensal</span>
                        <span className="text-sm font-medium text-slate-800">
                          {(parseFloat(selectedExpense.amount) / total * 100).toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex gap-2 mt-6">
                    <button
                      onClick={() => {
                        setEditData({
                          description: selectedExpense.description,
                          amount: parseFloat(selectedExpense.amount),
                          category: selectedExpense.category,
                          date: new Date(selectedExpense.date).toISOString().split('T')[0],
                          recurrence: selectedExpense.recurrence,
                          recurrenceEnd: selectedExpense.recurrenceEnd ? new Date(selectedExpense.recurrenceEnd).toISOString().split('T')[0] : '',
                          notes: selectedExpense.notes || '',
                        });
                        setEditing(true);
                      }}
                      className="flex-1 py-2.5 rounded-xl bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => {
                        setDescription(selectedExpense.description);
                        setAmount(String(parseFloat(selectedExpense.amount)));
                        setCategory(selectedExpense.category || '');
                        setDate(new Date().toISOString().split('T')[0]);
                        setRecurrence('none');
                        setRecurrenceEnd('');
                        setNotes(selectedExpense.notes || '');
                        setSelectedExpense(null);
                        setShowModal(true);
                      }}
                      className="flex-1 py-2.5 rounded-xl border border-slate-200 text-sm text-slate-600 hover:bg-slate-50 transition font-medium"
                    >
                      Duplicar
                    </button>
                    <button
                      onClick={() => { setDeleteTarget(selectedExpense); setSelectedExpense(null); setEditing(false); }}
                      className="flex-1 py-2.5 rounded-xl border border-red-200 text-sm text-red-500 hover:bg-red-50 transition font-medium"
                    >
                      Remover
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium animate-fade-in ${
          toast.type === 'upgrade'
            ? 'bg-amber-500 text-white cursor-pointer'
            : toast.type === 'error'
              ? 'bg-red-500 text-white'
              : 'bg-emerald-500 text-white'
        }`} onClick={toast.type === 'upgrade' ? () => navigate('/account?tab=plans') : undefined}>
          {toast.message}
          {toast.type === 'upgrade' && <span className="ml-2 underline">Ver planos</span>}
        </div>
      )}
    </div>
  );
}
